import React, {useId, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Heading, Text, useSlotRecipe} from '@chakra-ui/react3'
import {defineMessages, useIntl} from 'react-intl'

import {NewsletterDecorationIcon} from '../icons'
import NewsletterForm from '../newsletter-form'
import useNewsletterForm from '../../../hooks/use-newsletter-form'
import {footerNewsletterRecipe} from './index.recipe'

export const FOOTER_NEWSLETTER_SOURCE = 'Web - Newsletter Footer'

const messages = defineMessages({
    signUpTitle: {
        defaultMessage: 'Begin your new chapter with Stokke®',
        id: 'leap_newsletter.sign_up.title'
    },
    signUpDescription: {
        defaultMessage:
            'From day one to the years ahead, sign up for our newsletter to receive product updates, early access, exclusive offers, and tailored guidance for every milestone.',
        id: 'leap_newsletter.sign_up.description'
    },
    confirmationTitle: {
        defaultMessage: 'And so, our story begins.',
        id: 'leap_newsletter.confirmation.title'
    },
    confirmationDescription: {
        defaultMessage:
            "We're delighted to walk alongside you with shared knowledge and special surprises for every step of the way. Here's to growing together!",
        id: 'leap_newsletter.confirmation.description'
    },
    confirmationPostscript: {
        defaultMessage:
            "P.S. Make sure you don't miss a chapter – add stokke@email.stokke.com to your address book.",
        id: 'leap_newsletter.confirmation.postscript'
    }
})

/**
 * Storefront newsletter displayed directly above the footer.
 *
 * @param {Object} props
 * @param {string} [props.source] - Sign-up source sent to the newsletter endpoint.
 * @returns {React.ReactElement}
 */
const FooterNewsletter = ({source = FOOTER_NEWSLETTER_SOURCE}) => {
    const {form, submitForm, recaptchaRef, isSubscribed, confirmationProps} = useNewsletterForm({
        source
    })
    const view = isSubscribed ? 'confirmation' : 'signUp'
    const headingId = useId()
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: footerNewsletterRecipe})
    const styles = useMemo(() => recipe({view}), [recipe, view])
    return (
        <Box as="section" css={styles.root} aria-labelledby={headingId}>
            <Box css={styles.content}>
                {isSubscribed ? (
                    <Box {...confirmationProps} css={styles.confirmation}>
                        <Heading as="h2" id={headingId} css={styles.heading} unstyled>
                            {formatMessage(messages.confirmationTitle)}
                        </Heading>
                        <Box css={styles.copyGroup}>
                            <Text css={styles.copy}>
                                {formatMessage(messages.confirmationDescription)}
                            </Text>
                            <Text css={styles.copy}>
                                {formatMessage(messages.confirmationPostscript)}
                            </Text>
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Box css={styles.signUp}>
                            <Box css={styles.textGroup}>
                                <Heading as="h2" id={headingId} css={styles.heading} unstyled>
                                    {formatMessage(messages.signUpTitle)}
                                </Heading>
                                <Text css={styles.copy}>
                                    {formatMessage(messages.signUpDescription)}
                                </Text>
                            </Box>
                            <NewsletterForm
                                form={form}
                                submitForm={submitForm}
                                recaptchaRef={recaptchaRef}
                                layout="condensed"
                                variant="filled"
                            />
                        </Box>
                        <Box css={styles.artwork}>
                            <NewsletterDecorationIcon css={styles.artworkImage} />
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    )
}

FooterNewsletter.propTypes = {
    source: PropTypes.string
}

export default FooterNewsletter
